// Asset preview — ENVIRONMENT tab: per-biome tileset frames + an autotile
// composition, the five parallax layers scrolling at their camera multipliers,
// and props / orbs / pickups (orbs with the in-game '?' overlay).

import Phaser from 'phaser';
import { TEX, TILE_FRAMES, TILESET_FRAME_COUNT, parallaxKey, tilesetKey } from '../../art/keys';
import { FONT, PARALLAX, PARALLAX_LAYERS, TILE } from '../../config/constants';
import { COLORS, css } from '../../config/palette';
import { BIOME_IDS, ORB_TYPES, type BiomeId } from '../../core/types';
import type { PreviewTab } from './tab';
import { Button, PV, drawPanel, label, placeholderTexture } from './widgets';

const BIOME_NAME: Record<BiomeId, string> = { graveyard: 'NEON GRAVEYARD', foundry: 'SKY FOUNDRY', vault: 'SINGULARITY VAULT' };
const BIOME_ACCENT: Record<BiomeId, number> = { graveyard: 0x3fd6c8, foundry: 0xff7a1a, vault: 0x9b6bff };

/** Autotile demo: '#' solid, '=' one-way, '^' hazard, 'b' back wall. */
const DEMO = [
  '..........bbbb.......',
  '..==......b##b..===..',
  '..........b##b.......',
  '#####.....####...####',
  '#####^^^^^#####..####',
  '################^####',
];

const ORB_COLOR = { arsenal: COLORS.orbArsenal, armor: COLORS.orbArmor, unstable: COLORS.orbUnstable } as const;

export class EnvTab implements PreviewTab {
  readonly root: Phaser.GameObjects.Container;
  private content: Phaser.GameObjects.Container;
  private biomeButtons = new Map<BiomeId, Button>();
  private biome: BiomeId = 'graveyard';
  private layers: { ts: Phaser.GameObjects.TileSprite; mult: number }[] = [];
  private orbs: { c: Phaser.GameObjects.Container; baseY: number; phase: number }[] = [];
  private masks: Phaser.GameObjects.Graphics[] = [];
  private scroll = 0;
  private speed = 220;

  constructor(private scene: Phaser.Scene) {
    this.root = scene.add.container(0, 0);
    this.content = scene.add.container(0, 0);
    BIOME_IDS.forEach((b, i) => {
      const btn = new Button(scene, PV.pad + i * 228, PV.headerH + 12, BIOME_NAME[b], () => this.select(b), {
        w: 220,
        h: 36,
        accent: BIOME_ACCENT[b],
        toggle: true,
        size: 14,
      });
      this.biomeButtons.set(b, btn);
      this.root.add(btn.root);
    });
    this.root.add(label(scene, PV.pad + 3 * 228 + 8, PV.headerH + 22, 'Q / E · +/- SCROLL SPEED', { font: 'mono', size: 12, color: COLORS.uiDim }));
    this.root.add(this.content);
    this.select('graveyard');
  }

  private select(b: BiomeId): void {
    this.biome = b;
    for (const [id, btn] of this.biomeButtons) btn.setActive(id === b);
    this.content.removeAll(true);
    for (const m of this.masks) m.destroy();
    this.masks = [];
    this.layers = [];
    this.orbs = [];
    const top = PV.headerH + 60;
    this.buildTiles(PV.pad, top, 560, 600);
    this.buildParallax(PV.pad + 576, top, PV.W - PV.pad * 2 - 576, 600);
    this.buildProps(PV.pad, top + 616, PV.W - PV.pad * 2, PV.H - PV.pad - top - 616);
  }

  private panel(x: number, y: number, w: number, h: number, title: string): void {
    const g = this.scene.add.graphics();
    drawPanel(g, x, y, w, h, { fill: COLORS.uiBg, fillAlpha: 0.8, chamfer: 12, accent: BIOME_ACCENT[this.biome] });
    this.content.add([g, label(this.scene, x + 16, y + 10, title, { font: 'display', size: 15, weight: 700, spacing: 3 })]);
  }

  private buildTiles(x: number, y: number, w: number, h: number): void {
    const s = this.scene;
    const key = tilesetKey(this.biome);
    this.panel(x, y, w, h, `TILESET · ${key}`);
    if (!s.textures.exists(key)) {
      this.content.add(label(s, x + w / 2, y + h / 2, 'TILESET NOT REGISTERED', { font: 'display', size: 16, color: COLORS.uiDim, originX: 0.5, spacing: 3 }));
      return;
    }
    const tex = s.textures.get(key);
    // Frame sheet: 8 columns, index under each frame.
    const cols = 8;
    const step = TILE + 18;
    const gx = x + (w - cols * step) / 2 + 9;
    for (let f = 0; f < TILESET_FRAME_COUNT; f++) {
      const fx = gx + (f % cols) * step;
      const fy = y + 46 + Math.floor(f / cols) * (step + 4);
      if (tex.has(String(f))) this.content.add(s.add.image(fx, fy, key, f).setOrigin(0, 0));
      this.content.add(label(s, fx + TILE / 2, fy + TILE + 1, String(f), { font: 'mono', size: 10, color: COLORS.uiDim, originX: 0.5 }));
    }
    // Autotile composition from the neighbour-mask rule.
    const cy = y + 46 + 4 * (step + 4) + 16;
    const scale = Math.min(1, (w - 40) / (DEMO[0].length * TILE), (y + h - cy - 16) / (DEMO.length * TILE));
    const ox = x + (w - DEMO[0].length * TILE * scale) / 2;
    const at = (c: number, r: number): string => DEMO[r]?.[c] ?? '.';
    const solid = (c: number, r: number): boolean => at(c, r) === '#' || at(c, r) === '^';
    for (let r = 0; r < DEMO.length; r++) {
      for (let c = 0; c < DEMO[r].length; c++) {
        const ch = at(c, r);
        let frame: number | null = null;
        if (ch === '#') {
          const mask = (solid(c, r - 1) ? 1 : 0) | (solid(c + 1, r) ? 2 : 0) | (solid(c, r + 1) ? 4 : 0) | (solid(c - 1, r) ? 8 : 0);
          frame = mask === 15 && (c * 7 + r * 3) % 5 === 0 ? TILE_FRAMES.solidDetailA : TILE_FRAMES.solidMaskBase + mask;
        } else if (ch === '^') frame = TILE_FRAMES.hazard;
        else if (ch === 'b') frame = TILE_FRAMES.backWall;
        else if (ch === '=') {
          const l = at(c - 1, r) === '=';
          const rr = at(c + 1, r) === '=';
          frame = l && rr ? TILE_FRAMES.platformMid : l ? TILE_FRAMES.platformRight : rr ? TILE_FRAMES.platformLeft : TILE_FRAMES.platformSingle;
        }
        if (frame !== null && tex.has(String(frame))) {
          this.content.add(s.add.image(ox + c * TILE * scale, cy + r * TILE * scale, key, frame).setOrigin(0, 0).setScale(scale));
        }
      }
    }
  }

  private buildParallax(x: number, y: number, w: number, h: number): void {
    const s = this.scene;
    this.panel(x, y, w, h, 'PARALLAX · 5 LAYERS');
    const vx = x + 16;
    const vy = y + 44;
    const vw = w - 32;
    const vh = h - 60 - 110;
    const bg = s.add.rectangle(vx, vy, vw, vh, 0x000000).setOrigin(0, 0);
    this.content.add(bg);
    const maskShape = s.make.graphics({}, false);
    maskShape.fillStyle(0xffffff).fillRect(vx, vy, vw, vh);
    this.masks.push(maskShape);
    const mask = maskShape.createGeometryMask();
    PARALLAX_LAYERS.forEach((layer, i) => {
      const key = parallaxKey(this.biome, layer);
      const ok = s.textures.exists(key);
      // Layer legend + thumbnail.
      const lx = vx + i * (vw / 5);
      const ly = vy + vh + 12;
      if (ok) {
        const src = s.textures.get(key).getSourceImage() as { width: number; height: number };
        const thumbH = 60;
        const thumb = s.add.image(lx, ly, key).setOrigin(0, 0);
        thumb.setScale(Math.min((vw / 5 - 12) / src.width, thumbH / src.height));
        this.content.add(thumb);
        const ts = s.add.tileSprite(vx, vy, vw, vh, key).setOrigin(0, 0);
        const k = vh / src.height;
        ts.setTileScale(k, k);
        ts.setMask(mask);
        this.content.add(ts);
        this.layers.push({ ts, mult: PARALLAX[layer] });
      } else {
        this.content.add(s.add.image(lx + 30, ly + 30, placeholderTexture(s, 'pv_missing_layer', 60, 60, COLORS.uiBorder)));
      }
      this.content.add(
        label(s, lx, ly + 66, `${layer.toUpperCase()} ×${PARALLAX[layer]}${ok ? '' : ' · MISSING'}`, {
          font: 'mono',
          size: 11,
          color: ok ? COLORS.uiText : COLORS.uiBad,
        }),
      );
    });
  }

  private buildProps(x: number, y: number, w: number, h: number): void {
    const s = this.scene;
    this.panel(x, y, w, h, 'PROPS · PICKUPS · ORBS');
    const entries: { key: string; frame?: number; name: string; orb?: (typeof ORB_TYPES)[number] }[] = [
      { key: TEX.door, frame: 0, name: 'door · locked' },
      { key: TEX.door, frame: 1, name: 'door · open' },
      { key: TEX.exitGate, frame: 0, name: 'exit · idle' },
      { key: TEX.exitGate, frame: 1, name: 'exit · active' },
      { key: TEX.crate, name: 'crate' },
      { key: TEX.terminal, name: 'terminal' },
      { key: TEX.station, name: 'station' },
      { key: TEX.repairBay, name: 'repair bay' },
      { key: TEX.holoBase, name: 'holo base' },
      { key: TEX.scrap, name: 'scrap' },
      { key: TEX.health, name: 'health' },
      { key: TEX.spawnPortal, name: 'portal' },
      { key: TEX.reticle, name: 'reticle' },
      ...ORB_TYPES.map((o) => ({ key: TEX.orbShell[o], name: `orb · ${o}`, orb: o })),
    ];
    const cellW = (w - 24) / entries.length;
    const boxH = h - 64;
    entries.forEach((e, i) => {
      const cx = x + 12 + i * cellW + cellW / 2;
      const cy = y + 40 + boxH / 2;
      const has = s.textures.exists(e.key) && (e.frame === undefined || s.textures.get(e.key).has(String(e.frame)));
      if (has) {
        const img = s.add.image(0, 0, e.key, e.frame);
        img.setScale(Math.min(1, (cellW - 8) / img.width, boxH / img.height));
        if (e.orb) {
          // Orb: shell + rendered '?' + gentle bob (matches the in-game presentation).
          const q = s.add
            .text(0, 0, '?', { fontFamily: FONT.display, fontSize: `${Math.round(img.displayHeight * 0.5)}px`, fontStyle: '700', color: '#ffffff' })
            .setOrigin(0.5)
            .setShadow(0, 0, css(ORB_COLOR[e.orb]), 12, true, true);
          const c = s.add.container(cx, cy, [img, q]);
          this.orbs.push({ c, baseY: cy, phase: i });
          this.content.add(c);
        } else {
          img.setPosition(cx, cy);
          this.content.add(img);
        }
      } else {
        this.content.add(label(s, cx, cy, 'MISSING', { font: 'mono', size: 10, color: COLORS.uiBad, originX: 0.5, originY: 0.5 }));
      }
      this.content.add(label(s, cx, y + h - 22, e.name, { font: 'mono', size: 10, color: COLORS.uiDim, originX: 0.5 }));
    });
  }

  update(dt: number): void {
    this.scroll += dt * this.speed;
    for (const l of this.layers) {
      // tilePosition is in texture space: divide the screen offset by the tile scale.
      l.ts.tilePositionX = (this.scroll * l.mult) / (l.ts.tileScaleX || 1);
    }
    const t = this.scene.time.now / 1000;
    for (const o of this.orbs) {
      o.c.y = o.baseY + Math.sin(t * 2 + o.phase) * 5;
      o.c.rotation = Math.sin(t * 0.8 + o.phase) * 0.08;
    }
  }

  key(code: string): boolean {
    const i = BIOME_IDS.indexOf(this.biome);
    if (code === 'KeyQ') this.select(BIOME_IDS[(i + BIOME_IDS.length - 1) % BIOME_IDS.length]);
    else if (code === 'KeyE') this.select(BIOME_IDS[(i + 1) % BIOME_IDS.length]);
    else if (code === 'Equal' || code === 'NumpadAdd') this.speed = Math.min(2000, this.speed * 1.5);
    else if (code === 'Minus' || code === 'NumpadSubtract') this.speed = Math.max(20, this.speed / 1.5);
    else return false;
    return true;
  }

  destroy(): void {
    for (const m of this.masks) m.destroy();
    this.masks = [];
    this.root.destroy();
  }
}
